import React, { useState } from 'react';
import { TylerState, updateReact } from '../../utils/TylerState';
import { ForecastAccount, CURRENCY_SYMBOLS } from '../../types/personalForecast';
import { parseHistoryPaste, ImportedRow } from '../../utils/historyImport';
import { addMonths, currentMonth } from '../../utils/forecastEngine';

interface Props {
  onClose: () => void;
}

let nextImportId = 1;

/**
 * Paste a block of spreadsheet rows (one account per line, balances across) and turn them
 * into accounts with monthly history. Everything happens in the browser.
 */
const HistoryImport: React.FC<Props> = ({ onClose }) => {
  const { accounts, settings } = TylerState.forecast;
  const today = currentMonth();
  const [text, setText] = useState('');
  const [firstMonth, setFirstMonth] = useState(settings.historyStartMonth || addMonths(today, -6));
  const [preview, setPreview] = useState<ReturnType<typeof parseHistoryPaste> | null>(null);
  const [applied, setApplied] = useState('');

  const existingByName = (name: string): ForecastAccount | undefined =>
    accounts.find(a => a.name.trim().toLowerCase() === name.trim().toLowerCase());

  const runPreview = () => {
    setApplied('');
    setPreview(parseHistoryPaste(text, firstMonth, settings.baseCurrency, today));
  };

  const apply = () => {
    if (!preview) return;
    let added = 0;
    let updated = 0;
    preview.rows.forEach((row: ImportedRow) => {
      const existing = existingByName(row.name);
      if (existing) {
        existing.actuals = { ...(existing.actuals || {}), ...row.balances };
        updated += 1;
      } else {
        const months = Object.keys(row.balances).sort();
        TylerState.forecast.accounts.push({
          id: `acct-import-${Date.now()}-${nextImportId++}`,
          name: row.name,
          currency: row.currency,
          bucket: row.bucket,
          side: row.side,
          openingBalance: row.balances[months[0]],
          interestRate: 0,
          growthRate: 0,
          sortOrder: TylerState.forecast.accounts.length,
          actuals: row.balances,
        });
        added += 1;
      }
    });
    // Pasting starts history tracking if needed, and reaches the grid back to the earliest pasted month.
    if (!settings.historyStartMonth || firstMonth < settings.historyStartMonth) {
      settings.historyStartMonth = firstMonth;
    }
    TylerState.forecast.isDirty = true;
    updateReact();
    setApplied(`Added ${added} account${added === 1 ? '' : 's'}, updated ${updated}. Remember to Save.`);
    setPreview(null);
    setText('');
  };

  const monthLabel = (month: string) =>
    new Date(month + '-01T00:00:00').toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });

  const money = (amount: number, currency: ImportedRow['currency']) =>
    `${amount < 0 ? '-' : ''}${CURRENCY_SYMBOLS[currency]}${Math.abs(amount).toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', background: '#f8fafc' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <strong>Paste from your spreadsheet</strong>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.1rem' }} title="Close">x</button>
      </div>
      <p className="help-text" style={{ marginTop: 0 }}>
        Copy a block of cells, one account per row, with the month-end balances across. Extra columns such as "BS", "$", "Savings" or "65%" are understood and used; the first other text cell is the account name. Brackets or a minus mean a liability; a dash leaves that month blank.
      </p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Questrade RRSP\t27,000\t28,400\t32,000\nMortgage 1\t(258,000)\t(257,000)\t(256,740)'}
        rows={6}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        <label style={{ fontSize: '0.9rem', color: '#64748b' }}>
          First balance column is{' '}
          <input type="month" value={firstMonth} max={today} onChange={e => setFirstMonth(e.target.value)} style={{ marginLeft: '6px', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
        </label>
        <button className="btn-secondary" onClick={runPreview} disabled={!text.trim()} style={{ padding: '6px 14px', fontSize: '0.9rem' }}>
          Preview
        </button>
        {applied && <span style={{ fontSize: '0.9rem', color: '#059669' }}>{applied}</span>}
      </div>

      {preview && (
        <div style={{ marginTop: '1rem' }}>
          {preview.rows.length === 0 ? (
            <p style={{ color: '#dc2626' }}>Nothing usable found. Each row needs a name and at least one number.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="accounts-table" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Currency</th>
                    <th>Bucket</th>
                    <th>Side</th>
                    <th>Months</th>
                    <th style={{ textAlign: 'right' }}>First</th>
                    <th style={{ textAlign: 'right' }}>Latest</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, i) => {
                    const months = Object.keys(row.balances).sort();
                    return (
                      <tr key={i}>
                        <td>{row.name}</td>
                        <td>{row.currency}</td>
                        <td style={{ textTransform: 'capitalize' }}>{row.bucket}</td>
                        <td style={{ color: row.side === 'asset' ? '#059669' : '#dc2626' }}>{row.side}</td>
                        <td>{monthLabel(months[0])} to {monthLabel(months[months.length - 1])} ({months.length})</td>
                        <td style={{ textAlign: 'right' }}>{money(row.balances[months[0]], row.currency)}</td>
                        <td style={{ textAlign: 'right' }}>{money(row.balances[months[months.length - 1]], row.currency)}</td>
                        <td>{existingByName(row.name) ? 'Update existing' : 'Create'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {preview.skipped.length > 0 && (
            <ul style={{ color: '#92400e', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {preview.skipped.map((reason, i) => <li key={i}>{reason}</li>)}
            </ul>
          )}
          {preview.droppedFutureColumns > 0 && (
            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
              Ignored {preview.droppedFutureColumns} value{preview.droppedFutureColumns === 1 ? '' : 's'} in columns after {monthLabel(today)}; history stops at the current month.
            </p>
          )}
          {preview.rows.length > 0 && (
            <button className="btn-primary" onClick={apply} style={{ marginTop: '0.75rem', padding: '8px 18px' }}>
              Add {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'} to my forecast
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryImport;
